import { BannerStatus, Button, Dialog, Text } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useDashboard } from '@/hooks/useDashboard';
import { UserApi } from '@/utilities/api/UserApi';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { ApiRetailer, ApiSubwalletBalance, ApiUser } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import Info from '@mui/icons-material/Info';
import Error from '@mui/icons-material/Error';
import Warning from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material';
import { daysFromDate, getJsDateFromString } from '@shared/utilities';
import { Modify } from '@shared/utilities/UtilityTypes';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

type DaysThreshold = 7 | 30 | 60 | 90;

interface ExpirationStatus {
  daysThreshold: DaysThreshold,
  isDismissible: boolean,
  status: BannerStatus,
}

const ExpirationStatuses: ExpirationStatus[] = [
  {
    daysThreshold: 7,
    isDismissible: false,
    status: 'error',
  },
  {
    daysThreshold: 30,
    isDismissible: true,
    status: 'warning',
  },
  {
    daysThreshold: 60,
    isDismissible: true,
    status: 'info',
  },
  {
    daysThreshold: 90,
    isDismissible: true,
    status: 'info',
  },
];

interface ProgramPointsExpirationModalProps {
  accessToken?: string,
  customer: ApiUser,
  programs: ApiRewardsProgram[],
  retailer: ApiRetailer,
}

const ProgramPointsExpirationLocalStorageKey = 'program-points-expiration-modal-dismissed';

export const ProgramPointsExpirationModal = ({
  accessToken,
  customer,
  programs,
  retailer,
}: ProgramPointsExpirationModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [status, setStatus] = useState<ExpirationStatus>();
  const [shouldShow, setShouldShow] = useState(false);
  const { data: dashboardData } = useDashboard(accessToken);
  const [earliestExpiringBalance, setEarliestExpiringBalance] = (
    useState<Modify<ApiSubwalletBalance, { expirationDate: string }>>()
  );

  const { data: subwalletBalances } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customer.id],
    () => UserApi.getSubwalletBalances(customer.id),
    { enabled: !accessToken },
  );

  useEffect(() => {
    const rewardProgramBalances = (subwalletBalances ?? dashboardData?.balances ?? []).filter(
      (balance) =>
        !!balance.rewardsProgramId
        && !balance.bookingId
        && balance.points > 0,
    ) as Modify<ApiSubwalletBalance, { expirationDate: string }>[];

    if (!rewardProgramBalances || !rewardProgramBalances.length) {
      return;
    }

    const soonestExpiringBalance = rewardProgramBalances.reduce((minBalance, balance) => (
      getJsDateFromString(balance.expirationDate) < getJsDateFromString(minBalance.expirationDate)
        ? balance : minBalance
    ));

    setEarliestExpiringBalance(soonestExpiringBalance);

    const daysUntilExpiration = Math.floor(
      -daysFromDate(getJsDateFromString(soonestExpiringBalance.expirationDate, { endOfDay: true })),
    );
    const statusToDisplay = ExpirationStatuses.find(
      (status) => status.daysThreshold >= daysUntilExpiration,
    );

    if (!statusToDisplay) {
      return;
    }

    setStatus(statusToDisplay);

    const dismissedData = LocalStorageUtility.getItem(ProgramPointsExpirationLocalStorageKey);
    if (dismissedData) {
      const { daysThreshold, dismissedAtDate, programId } = JSON.parse(dismissedData);
      if (
        programId === soonestExpiringBalance.rewardsProgramId
        && statusToDisplay.daysThreshold >= parseInt(daysThreshold)
        && (
          statusToDisplay.isDismissible
          || dismissedAtDate === DateTime.now().toISODate()
        )
      ) {
        return;
      }
    }

    setShouldShow(true);
  }, [dashboardData, subwalletBalances]);

  const handleDismiss = () => {
    if (!status || !earliestExpiringBalance) {
      return;
    }
    const dismissedData: { daysThreshold: number, dismissedAtDate: string, programId: string } = {
      daysThreshold: status.daysThreshold,
      dismissedAtDate: DateTime.now().toISODate(),
      programId: earliestExpiringBalance.rewardsProgramId!,
    };
    LocalStorageUtility.set(ProgramPointsExpirationLocalStorageKey, dismissedData);
    setShouldShow(false);
  };

  if (!shouldShow || !status) {
    return null;
  }

  const getDayText = () => {
    if (!earliestExpiringBalance) {
      return '';
    }
    const daysUntilExpiration = Math.floor(
      -daysFromDate(
        getJsDateFromString(earliestExpiringBalance.expirationDate, { endOfDay: true }),
      ),
    );
    if (daysUntilExpiration === 0) {
      return t($ => $.customerDashboard.pointsExpiration.banner.expiringToday);
    }
    return t(
      $ => $.customerDashboard.pointsExpiration.banner.expiringInDays,
      { count: daysUntilExpiration },
    );
  };

  const getIcon = () => {
    let Icon = null;
    switch (status.status) {
      case 'info':
        Icon = Info;
        break;
      case 'warning':
        Icon = Warning;
        break;
      case 'error':
        Icon = Error;
        break;
      default:
        return null;
    }
    return <Icon color={status.status} fontSize="large" />;
  };

  if (!earliestExpiringBalance) {
    return null;
  }

  const matchingProgram = programs.find(
    (program) => program.id === earliestExpiringBalance.rewardsProgramId,
  );
  if (!matchingProgram) {
    return null;
  }

  const programStartDate = getJsDateFromString(matchingProgram.startDate);
  const programEndDate = getJsDateFromString(matchingProgram.endDate, { endOfDay: true });
  const startFullYear = programStartDate.getFullYear();
  const endYearShort = programEndDate.getFullYear() % 100;
  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          color="inherit"
          onClick={handleDismiss}
          sx={{ borderColor: '#E0E0E0' }}
          variant="outlined"
        >
          {t($ => $.common.actions.close)}
        </Button>
      )}
      heroIcon={getIcon()}
      onClose={() => void 0}
      open
      showCloseButton={false}
      testID="program-expiration-modal"
      title={t($ => $.customerDashboard.programExpiration.modal.title, {
        retailerName: retailer.name,
        timeframe: getDayText(),
        yearEnd: endYearShort,
        yearStart: startFullYear,
      })}
    >
      <Text category="body-medium" sx={{ color: theme.palette.text.secondary }} textAlign="center">
        {t($ => $.customerDashboard.programExpiration.modal.programPeriod, {
          startDate: programStartDate,
          endDate: programEndDate,
        })}
      </Text>
    </Dialog>
  );
};
